import { Descendant, Editor, Path, Element as SlateElement, Text, Transforms } from 'slate'

import { BLOCK_FORMATS } from 'core-components/library/richTextEditor'

export const withExpandBlock = (editor: Editor) => {
  const { insertSoftBreak, normalizeNode, deleteBackward } = editor

  editor.insertSoftBreak = () => {
    const { selection } = editor

    if (!selection) {
      return insertSoftBreak()
    }

    const [expandTitleNode] = Editor.nodes(editor, {
      match: (node) => SlateElement.isElement(node) && node.type === BLOCK_FORMATS.expandBlockTitle,
    })

    if (expandTitleNode) {
      editor.insertText('\n')
      return
    }

    insertSoftBreak()
  }

  editor.normalizeNode = (entry) => {
    const blocks = Array.from(
      Editor.nodes(editor, {
        at: [],
        match: (n) => SlateElement.isElement(n) && n.type === BLOCK_FORMATS.expandBlock,
      })
    )

    const result = blocks.reduce<{
      blockWithZeroTitlesPath: Path | null
      extraTitlePath: Path | null
    }>(
      (acc, [blockNode, blockPath]) => {
        if (acc.blockWithZeroTitlesPath && acc.extraTitlePath) {
          return acc
        }

        if (!SlateElement.isElement(blockNode)) {
          return acc
        }

        const blockNodeChildren: Descendant[] = blockNode.children

        const titleIndices = blockNodeChildren.reduce((indexesAcc: number[], child, index) => {
          if (SlateElement.isElement(child) && child.type === BLOCK_FORMATS.expandBlockTitle) {
            return [...indexesAcc, index]
          }
          return indexesAcc
        }, [])

        if (!titleIndices.length && !acc.blockWithZeroTitlesPath) {
          return {
            ...acc,
            blockWithZeroTitlesPath: blockPath,
          }
        }

        if (titleIndices.length > 1 && !acc.extraTitlePath) {
          const secondTitleIndex = titleIndices[1]
          return {
            ...acc,
            extraTitlePath: [...blockPath, secondTitleIndex],
          }
        }

        return acc
      },
      { blockWithZeroTitlesPath: null, extraTitlePath: null }
    )

    const { blockWithZeroTitlesPath, extraTitlePath } = result

    if (blockWithZeroTitlesPath && extraTitlePath) {
      Transforms.moveNodes(editor, {
        at: extraTitlePath,
        to: [...blockWithZeroTitlesPath, 0],
      })
      return
    }

    normalizeNode(entry)
  }

  editor.deleteBackward = (unit) => {
    const { selection } = editor

    if (!selection) {
      return deleteBackward(unit)
    }

    const textPath = selection.focus.path
    const summaryPath = Path.parent(textPath)

    const [summaryNode] = Editor.node(editor, summaryPath)

    const isSummaryStart =
      SlateElement.isElement(summaryNode) &&
      summaryNode.type === BLOCK_FORMATS.expandBlockTitle &&
      Editor.isStart(editor, selection.focus, textPath)

    if (!isSummaryStart) {
      return deleteBackward(unit)
    }

    const [parentBlock, parentPath] = Editor.parent(editor, summaryPath)
    const summaryIndex = summaryPath[summaryPath.length - 1]

    const isFirstInBlock =
      SlateElement.isElement(parentBlock) &&
      parentBlock.type === BLOCK_FORMATS.expandBlock &&
      summaryIndex === 0

    if (!isFirstInBlock) {
      return deleteBackward(unit)
    }

    const [grandParent] = Editor.parent(editor, parentPath)

    if (!SlateElement.isElement(grandParent)) {
      return deleteBackward(unit)
    }

    const blockIndex = parentPath[parentPath.length - 1]

    const hasParagraphWithTextBefore = grandParent.children
      .slice(0, blockIndex)
      .some((child: Descendant) => {
        const isParagraph = SlateElement.isElement(child) && child.type === BLOCK_FORMATS.paragraph
        return (
          isParagraph &&
          child.children.some(
            (textNode: Descendant) => Text.isText(textNode) && textNode.text.length > 0
          )
        )
      })

    if (hasParagraphWithTextBefore) {
      return
    }

    deleteBackward(unit)
  }

  return editor
}
